/**
 * AccessPanel - one person's access, area by area (Team page, "Access").
 *
 * Every area gets a Hidden / View / Edit choice on top of the person's role
 * (Sharif, 10/5/26). "Role default" means no override: the area follows the
 * role, now and after any later role change. An override is highlighted, so
 * what differs from the role is visible at a glance. "Reset to role" drops
 * every override. The "Can manage access" switch is for the people who
 * manage access - the only people this panel opens for.
 *
 * Reads and writes /api/users/:email/access (server/routes/users.js). Each
 * change is ONE request, so the Activity log tells it as one line; the server
 * answers with the whole new picture, which replaces what is shown.
 */
import { useCallback, useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { LEVEL_LABELS, roleLabel } from '../../data/roles';

const GREEN = '#1B5E20';
const OVERRIDE_BG = '#FFF8E1';
const OVERRIDE_BORDER = '#F9A825';
const TH = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 10,
  fontWeight: 800,
  color: '#888',
  letterSpacing: 1,
  textTransform: 'uppercase',
  borderBottom: `2px solid ${GREEN}`,
};
const TD = { padding: '8px 10px', fontSize: 12.5, color: '#0D0D0D', borderBottom: '1px solid #EEE', verticalAlign: 'middle' };

const accessUrl = (email) => `/api/users/${encodeURIComponent(email)}/access`;

async function send(email, body) {
  const res = await fetch(accessUrl(email), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const out = await res.json().catch(() => null);
  if (!res.ok) throw new Error((out && out.error) || `Could not save (HTTP ${res.status})`);
  return out;
}

export default function AccessPanel({ email, isSelf = false, onClose, onChanged }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(accessUrl(email))
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error((body && body.error) || `Could not load (HTTP ${r.status})`);
        return body;
      })
      .then((v) => {
        if (live) setView(v);
      })
      .catch((e) => {
        if (live) setError(e.message);
      });
    return () => {
      live = false;
    };
  }, [email]);

  const change = useCallback(
    async (body) => {
      setSaving(true);
      setError('');
      try {
        setView(await send(email, body));
        if (onChanged) onChanged();
      } catch (e) {
        setError(e.message);
      } finally {
        setSaving(false);
      }
    },
    [email, onChanged]
  );

  const overrides = view ? view.areas.filter((a) => a.override) : [];
  const fixed = !!(view && view.bootstrap);
  const resetToRole = () => {
    if (
      !window.confirm(
        `Drop ${overrides.length === 1 ? 'the 1 override' : `all ${overrides.length} overrides`} and give ${view.name || view.email} exactly what the ${roleLabel(view.role)} role gives?`
      )
    )
      return;
    change({ reset: true });
  };

  return (
    <Modal title="Access" onClose={onClose} width={640}>
      <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
        {!view && !error && <div style={{ fontSize: 12, color: '#888', padding: '12px 0' }}>Loading access…</div>}
        {error && (
          <div
            role="alert"
            style={{
              background: '#FFEBEE',
              border: '1px solid #FFCDD2',
              color: '#B71C1C',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 10,
            }}
          >
            {error}
          </div>
        )}
        {view && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0D0D0D' }}>{view.name || view.email}</div>
              <div style={{ fontSize: 11.5, color: '#666' }}>
                {view.email} · Role: <b>{roleLabel(view.role)}</b>
                {overrides.length > 0 && (
                  <>
                    {' '}
                    · <b data-testid="override-count">{overrides.length}</b> {overrides.length === 1 ? 'override' : 'overrides'}
                  </>
                )}
              </div>
              {fixed && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: '#555' }}>
                  The bootstrap admin always has full access and always manages access. Nothing here can change that.
                </div>
              )}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Area</th>
                  <th style={TH}>Access</th>
                  <th style={TH}>From</th>
                </tr>
              </thead>
              <tbody>
                {view.areas.map((a) => {
                  const own = !!a.override;
                  return (
                    <tr
                      key={a.key}
                      data-testid={`area-${a.key}`}
                      data-override={own ? 'yes' : 'no'}
                      style={{ background: own ? OVERRIDE_BG : 'transparent' }}
                    >
                      <td style={{ ...TD, fontWeight: 700 }}>{a.label}</td>
                      <td style={TD}>
                        <select
                          aria-label={`${a.label} access`}
                          value={a.override || ''}
                          disabled={fixed || saving}
                          onChange={(e) => change({ area: a.key, level: e.target.value || null })}
                          style={{
                            padding: '5px 8px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontFamily: 'inherit',
                            fontWeight: 600,
                            border: `1px solid ${own ? OVERRIDE_BORDER : '#D8DED4'}`,
                            background: '#fff',
                            color: '#0D0D0D',
                          }}
                        >
                          <option value="">Role default ({LEVEL_LABELS[a.role]})</option>
                          {a.levels.map((l) => (
                            <option key={l} value={l}>
                              {LEVEL_LABELS[l]}
                            </option>
                          ))}
                        </select>
                        {a.noDelete && a.effective === 'edit' && (
                          <span style={{ marginLeft: 8, fontSize: 10.5, color: '#888' }}>no delete (role)</span>
                        )}
                      </td>
                      <td style={{ ...TD, fontSize: 11 }}>
                        {own ? (
                          <span style={{ fontWeight: 800, color: '#8D6E00' }}>Override</span>
                        ) : (
                          <span style={{ color: '#888' }}>Role</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, flexWrap: 'wrap' }}
            >
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: '#0D0D0D',
                  cursor: fixed || isSelf ? 'not-allowed' : 'pointer',
                }}
                title={
                  fixed
                    ? 'Always on for the bootstrap admin'
                    : isSelf
                      ? 'Nobody can change their own access-management switch'
                      : 'May change roles and access for everyone'
                }
              >
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Can manage access"
                  checked={!!view.canManageAccess}
                  disabled={fixed || isSelf || saving}
                  onChange={(e) => change({ canManageAccess: e.target.checked })}
                />
                Can manage access
              </label>
              <button
                type="button"
                onClick={resetToRole}
                disabled={fixed || saving || overrides.length === 0}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: `1px solid ${GREEN}`,
                  background: '#fff',
                  color: GREEN,
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: fixed || saving || overrides.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: fixed || overrides.length === 0 ? 0.5 : 1,
                }}
              >
                Reset to role
              </button>
            </div>
            {isSelf && !fixed && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
                This is you: your own access-management switch can only be changed by someone else.
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
